import { useEffect, useRef, useState } from 'react'

// Singleton ResizeObserver instance shared across all components
let resizeObserver: ResizeObserver | null = null
const callbacks = new Map<Element, (entry: ResizeObserverEntry) => void>()

function getResizeObserver() {
  if (!resizeObserver) {
    resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const callback = callbacks.get(entry.target)
        callback?.(entry)
      })
    })
  }
  return resizeObserver
}

interface UseResizeObserverOptions {
  debounce?: number
  enabled?: boolean
}

/**
 * Centralized ResizeObserver hook with debouncing support
 * Uses a singleton ResizeObserver instance for better performance
 * 
 * @param callback - Function to call when element resizes
 * @param options - Configuration options
 * @returns ref to attach to the element you want to observe
 */
export function useResizeObserver<T extends HTMLElement>(
  callback: (entry: ResizeObserverEntry) => void,
  options: UseResizeObserverOptions = {}
) {
  const { debounce = 0, enabled = true } = options
  const ref = useRef<T>(null)
  const callbackRef = useRef(callback)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // Keep callback ref up to date
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    const element = ref.current
    if (!element || !enabled) return

    const observer = getResizeObserver()
    
    const debouncedCallback = (entry: ResizeObserverEntry) => {
      if (debounce > 0) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
        timeoutRef.current = setTimeout(() => {
          callbackRef.current(entry)
        }, debounce)
      } else {
        callbackRef.current(entry)
      }
    }

    callbacks.set(element, debouncedCallback)
    observer.observe(element)

    return () => {
      callbacks.delete(element)
      observer.unobserve(element)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [debounce, enabled])

  return ref
}

/**
 * Hook to observe element size changes and return dimensions
 * 
 * @param options - Configuration options
 * @returns [ref, { width, height }]
 */
export function useElementSize<T extends HTMLElement>(
  options: UseResizeObserverOptions = {}
) {
  const [size, setSize] = useState({ width: 0, height: 0 })
  
  const ref = useResizeObserver<T>((entry) => {
    const { width, height } = entry.contentRect
    setSize({ width, height })
  }, options)

  return [ref, size] as const
}
